'use client';

import { useState } from 'react';
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Rocket,
  Send,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PageShell } from '@/components/design/PageShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useI18n } from '@/contexts/I18nContext';

export default function CareersPage() {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.position || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const response = await fetch('/api/careers/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit application');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', position: '', phone: '', message: '' });
    } catch (error) {
      console.error('Application form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const setField = (key: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const principles = [
    { icon: Rocket, title: t('careers.newCategory'), description: t('careers.newCategoryDesc') },
    { icon: Users, title: t('careers.smallTeam'), description: t('careers.smallTeamDesc') },
    { icon: TrendingUp, title: t('careers.growthPath'), description: t('careers.growthPathDesc') },
  ];

  const roleOptions = [
    { value: 'frontend', label: t('careers.roleFrontend') },
    { value: 'backend', label: t('careers.roleBackend') },
    { value: 'fullstack', label: t('careers.roleFullstack') },
    { value: 'ai-ml', label: t('careers.roleAi') },
    { value: 'product', label: t('careers.roleProduct') },
    { value: 'design', label: t('careers.roleDesign') },
    { value: 'marketing', label: t('careers.roleMarketing') },
    { value: 'sales', label: t('careers.roleSales') },
    { value: 'other', label: t('careers.roleOther') },
  ];

  return (
    <PageShell>
      <section className="relative pt-4 sm:pt-36">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[660px] bg-brand-mesh opacity-80" />
        <div className="section-shell">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.78fr] lg:items-center">
            <div>
              <div className="section-kicker">{t('careers.eyebrow')}</div>
              <h1 className="mt-6 max-w-5xl text-balance text-5xl font-semibold leading-[0.98] tracking-normal text-ink-950 sm:text-6xl lg:text-7xl">
                {t('careers.title')}
              </h1>
              <p className="body-copy mt-6 max-w-2xl">
                {t('careers.description')}
              </p>
            </div>

            <div className="soft-card p-6 sm:p-8">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-glow">
                <Building2 className="h-5 w-5" />
              </div>
              <h2 className="text-3xl font-semibold tracking-normal text-ink-950">
                {t('careers.officeTitle')}
              </h2>
              <p className="mt-3 text-sm leading-6 text-ink-600">
                {t('careers.officeDesc')}
              </p>
              <div className="mt-6 rounded-[1.35rem] border border-ink-950/10 bg-ink-50 p-5 text-sm leading-6 text-ink-700">
                <p>{t('common.companyAddressLine1')}</p>
                <p>{t('common.companyAddressLine2')}</p>
                <p>{t('common.companyAddressLine3')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-14">
        <div className="grid gap-4 md:grid-cols-3">
          {principles.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="soft-card p-6">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-semibold text-ink-950">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-ink-600">{item.description}</p>
              </article>
            );
          })}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.72fr_1fr]">
          <aside className="soft-card p-6 sm:p-8">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-ink-950 text-white">
              <BriefcaseBusiness className="h-5 w-5" />
            </div>
            <h2 className="text-3xl font-semibold tracking-normal text-ink-950">
              {t('careers.rolesTitle')}
            </h2>
            <div className="mt-6 grid gap-3">
              {roleOptions.slice(0, 6).map((role) => (
                <div key={role.value} className="flex items-center justify-between rounded-2xl border border-ink-950/10 bg-ink-50 px-4 py-3 text-sm font-semibold text-ink-950">
                  {role.label}
                  <ArrowRight className="h-4 w-4 text-brand-600" />
                </div>
              ))}
            </div>
          </aside>

          <div className="soft-card p-6 sm:p-8">
            <div className="mb-7 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-glow">
                <Send className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-ink-950">{t('careers.applyTitle')}</h2>
                <p className="text-sm text-ink-500">{t('careers.applyDesc')}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Input label={t('careers.fullName')} value={formData.name} onChange={(e) => setField('name', e.target.value)} required />
                <Input label={t('common.email')} type="email" value={formData.email} onChange={(e) => setField('email', e.target.value)} required />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">{t('careers.position')}</label>
                  <Select
                    value={formData.position}
                    onChange={(value) => setField('position', value)}
                    options={roleOptions}
                    placeholder={t('careers.selectRole')}
                  />
                </div>
                <Input label={t('careers.phone')} value={formData.phone} onChange={(e) => setField('phone', e.target.value)} />
              </div>
              <Textarea
                placeholder={t('careers.about')}
                value={formData.message}
                onChange={(e) => setField('message', e.target.value)}
                required
                className="min-h-[170px]"
              />

              {submitStatus === 'success' && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                  {t('careers.success')}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {t('careers.error')}
                </div>
              )}

              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('careers.submitting') : t('careers.submit')}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
